import { practiceFeatures } from "../config/practice";

/*
 * The unified Inbox's queues (owner 10-20, Step 1). One URL each; a queue
 * shows only where the practice has it (`feature`) and only for a login
 * holding ANY of its care Side Bar privileges (`privileges`, [] = every staff
 * login, as the page it embeds was before). Routes (routes/routes.js) use the
 * same table: a one-privilege queue is gated by the route, a several-privilege
 * queue (and All open) by InboxPage itself.
 *
 *   /inbox            All open: the counts of every queue + open Requests
 *   /inbox/requests   website appointment + new-patient requests and
 *                     onboarding intakes, one list (v2 /inbox/requests);
 *                     "Onboarding" OR "Communication" (review 10-20: the
 *                     appointment requests were a Communication tab)
 *   /inbox/messages   Messaging (fax/messaging.jsx)          "Communication"
 *   /inbox/faxes      Fax (fax/faxtable.jsx)                  "Communication"
 *   /inbox/voicemail  Voice mail (C-HIM's extensions)         "Communication"
 *   /inbox/email      Email (C-HIM's mailbox mirror)          "Communication"
 *   /inbox/forms      Form Inbox (Form Wizard), ungated as /form-inbox
 *
 * OSA (7825) keeps unifiedInbox off (config/practice.js). Its own queues are
 * mapped here already for the day it is switched on: Requests = Online
 * Inquiries ("Website Inquiry" privilege), Messages = its call-notes page
 * (ungated, as /messaging is there).
 */
export const INBOX_QUEUES = [
  { id: "all", path: "/inbox", label: "All open", privileges: [] },
  { id: "requests", path: "/inbox/requests", label: "Requests", privileges: ["Onboarding", "Communication"], feature: "websiteLeads" },
  { id: "messages", path: "/inbox/messages", label: "Messages", privileges: ["Communication"] },
  { id: "faxes", path: "/inbox/faxes", label: "Faxes", privileges: ["Communication"], feature: "fax" },
  { id: "voicemail", path: "/inbox/voicemail", label: "Voice mail", privileges: ["Communication"], feature: "chimCommunication" },
  { id: "email", path: "/inbox/email", label: "Email", privileges: ["Communication"], feature: "chimCommunication" },
  { id: "forms", path: "/inbox/forms", label: "Forms", privileges: [], feature: "formWizard" },
];

// The care Side Bar codes (utils/previlagecodes.js) that grant the Inbox entry
// in an embedded (?allowtoken=&sb=) side nav: its own, Communication's, Onboard's.
export const INBOX_SB_CODES = ["HIB", "HCM", "HON"];

// Per-practice differences (OSA's own pages and privileges).
function queueFor(q, features) {
  if (q.id === "requests" && features.webInquiry) return { ...q, privileges: ["Website Inquiry"], feature: "webInquiry" };
  if (q.id === "messages" && features.messagingPage) return { ...q, privileges: [] };
  return q;
}

// The practice's queues (privileges not applied).
export function practiceInboxQueues(features = practiceFeatures()) {
  return INBOX_QUEUES.map((q) => queueFor(q, features)).filter((q) => !q.feature || features[q.feature]);
}

// The route privilege for a queue: its one privilege, else "" (InboxPage checks).
export function routePrivilege(q) {
  return q.privileges.length === 1 ? q.privileges[0] : "";
}

export function queueAllowed(q, privileges) {
  const held = Array.isArray(privileges) ? privileges : [];
  return !q.privileges.length || q.privileges.some((p) => held.includes(p));
}

// The queues this login can open: none without any of the Inbox's
// privileges (inboxNavPrivileges; the side-nav rule), else every allowed one
// ("All open" and the ungated Forms included).
export function inboxQueues(privileges, features = practiceFeatures()) {
  const all = practiceInboxQueues(features);
  const need = inboxNavPrivileges(features);
  const held = Array.isArray(privileges) ? privileges : [];
  if (need.length && !need.some((p) => held.includes(p))) return [];
  return all.filter((q) => queueAllowed(q, held));
}

// The queue a path names (exact match), or null.
export function queueForPath(pathname, features = practiceFeatures()) {
  const p = String(pathname || "").replace(/\/+$/, "") || "/";
  return practiceInboxQueues(features).find((q) => q.path === p) || null;
}

// Where the old pages go when the Inbox is on (their query string is kept).
export const OLD_PATH_REDIRECTS = {
  "/fax": "/inbox/faxes",
  "/onboarding": "/inbox/requests",
  "/messaging": "/inbox/messages",
};

// The side-nav item's privileges: any queue's.
export function inboxNavPrivileges(features = practiceFeatures()) {
  return [...new Set(practiceInboxQueues(features).flatMap((q) => q.privileges))];
}
